// ---------------------------------------------------------------------------
// Estilos do Input Drawer V3.
// ---------------------------------------------------------------------------
// Port direto das regras CSS do protótipo
// (sandbox/inputs_v3_drawer_proposals.html) para makeStyles do Material-UI
// v4, com uma única diferença: a cor primária deixa de ser hardcoded e
// passa a receber `clientColor` (cor do tema do cliente) via props.
//
// Mantém as métricas do drawer V2 (height 98vh, margin 1vh, borderRadius
// 10px). Largura fixa de 1000px para acomodar rail (190px) + conteúdo.
// ---------------------------------------------------------------------------

import { makeStyles } from "@material-ui/core/styles";
import {
  grayColor,
  whiteColor,
  successColor,
  warningColor,
  dangerColor,
  infoColor,
} from "../../../../assets/jss/material-dashboard-pro-react";

export const DRAWER_WIDTH = 1000;

// Converte hex "#rrggbb" em "r, g, b" para uso dentro de rgba() dinâmico.
// Fallback: cor do tema quando o hex vier inválido.
function hexToRgbTriplet(hex) {
  if (!hex || typeof hex !== "string") return "214, 52, 45";
  const h = hex.replace("#", "");
  if (h.length !== 6) return "214, 52, 45";
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return "214, 52, 45";
  return `${r}, ${g}, ${b}`;
}

// Versão "escurecida" de um hex para hover do botão primário.
// Sem depender de lib — approximately 20% darker.
function darkenHex(hex) {
  if (!hex || typeof hex !== "string") return hex;
  const h = hex.replace("#", "");
  if (h.length !== 6) return hex;
  const r = Math.max(0, parseInt(h.substring(0, 2), 16) - 46);
  const g = Math.max(0, parseInt(h.substring(2, 4), 16) - 46);
  const b = Math.max(0, parseInt(h.substring(4, 6), 16) - 46);
  return `rgb(${r}, ${g}, ${b})`;
}

// Tokens tipográficos alinhados ao HTML demo.
const FONT_FAMILY = '"Roboto", sans-serif';

const useDrawerStyles = makeStyles((theme) => ({
  // ----- Overlay e wrapper -----------------------------------------------
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.08)",
    zIndex: 1299,
    opacity: 0,
    transition: "opacity 180ms ease",
    pointerEvents: "none",
  },
  overlayOpen: {
    opacity: 1,
    pointerEvents: "auto",
  },
  drawer: {
    position: "fixed",
    top: "1vh",
    right: "1vh",
    bottom: "1vh",
    width: DRAWER_WIDTH,
    maxWidth: "calc(100vw - 2vh)",
    background: whiteColor,
    borderRadius: 10,
    boxShadow:
      "0 12px 40px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    fontSize: 13,
    fontFamily: FONT_FAMILY,
    fontWeight: 300,
    color: grayColor[2],
    zIndex: 1300,
    transform: "translateX(calc(100% + 2vh))",
    transition: "transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)",
  },
  drawerOpen: {
    transform: "translateX(0)",
  },

  // ----- Header -----------------------------------------------------------
  // Header do drawer alinhado ao esquema do ProgressDashboardModal:
  // background escuro (#353534), conteúdo em branco puro. Cria uma
  // "capa" que contrasta com o corpo branco e reforça hierarquia
  // visual entre as telas gêmeas da V3.
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "10px 14px",
    background: "#353534",
    flexShrink: 0,
  },
  clientStrip: {
    width: 4,
    height: 38,
    borderRadius: 2,
    background: (props) => props.clientColor || dangerColor[2],
  },
  headerMain: {
    minWidth: 0,
    flex: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 500,
    margin: 0,
    color: whiteColor,
    letterSpacing: "-0.01em",
    lineHeight: 1.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  drawerSubtitle: {
    fontSize: 12,
    color: "rgba(255,255,255,0.72)",
    display: "flex",
    gap: 10,
    alignItems: "center",
    "& strong": {
      color: whiteColor,
      fontWeight: 500,
    },
  },
  sep: {
    width: 3,
    height: 3,
    background: "rgba(255,255,255,0.45)",
    borderRadius: "50%",
    flexShrink: 0,
  },
  periodPill: {
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 11px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.12)",
    color: whiteColor,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    flexShrink: 0,
  },
  // Status pill — mesma simbologia do cartão principal (classifyInputState).
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 11px",
    borderRadius: 999,
    background: grayColor[12],
    color: grayColor[1],
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    flexShrink: 0,
  },
  // Onda 2026-05-28: padronizacao da paleta dos pills de status do
  // header do drawer com a legenda da UI principal
  // (``INPUT_STATE_BACKGROUNDS`` em ``inputsV3StateRules.js``).
  // Diretrizes:
  //   - HUE do pill = HUE do swatch da legenda (mesma familia de cor).
  //   - Background SOLIDO (nao translucido sobre o ``clientColor``):
  //     pills ficam legiveis independentemente do tema do cliente.
  //   - Texto BRANCO em todos: garante WCAG AA mesmo sobre headers de
  //     clientes com fundo amarelo, vermelho, verde etc.
  //   - Quando o canonico e' muito claro (in_progress #a5d6a7,
  //     in_progress_attention #ffcc80) usamos a variante mais saturada
  //     da mesma familia, suficiente para texto branco e visualmente
  //     equivalente ao swatch (mesmo "verde de avancar", mesmo
  //     "laranja de atencao em avancar").
  statusPillOk: {
    background: successColor[3], // delivered_ok (mesma cor do swatch)
    color: whiteColor,
  },
  statusPillWarn: {
    background: warningColor[2], // attention (mesma cor do swatch)
    color: whiteColor,
  },
  statusPillErr: {
    background: dangerColor[2], // critical (mesma cor do swatch)
    color: whiteColor,
  },
  // Onda 1.3 / D-35: ``system_error`` (parser ou pipeline travou por
  // motivo tecnico, NAO por dado do cliente) usa pill PRETA. A
  // distincao visual e' importante: critico vermelho == "cliente
  // precisa agir" (ajustar o arquivo); preto == "Integration precisa
  // agir" (consertar pipeline). Cor interna alinhada ao
  // ``MONTH_STATUS_LEGEND.system_error`` em ``inputsV3StateRules.js``.
  statusPillSystemError: {
    background: "#000000",
    color: whiteColor,
  },
  statusPillPending: {
    // pending_decision na legenda usa hachurado warning+danger; aqui
    // mantemos solido com a cor canonica ``PENDING_DECISION_ORANGE``
    // para que o pill se leia bem como rotulo curto. O hachurado fica
    // reservado para o swatch da legenda e celulas do overview, onde
    // ele tem area suficiente para ser percebido.
    background: "#ed6c02",
    color: whiteColor,
  },
  statusPillProcessing: {
    // Excecao do padrao "branco em tudo": o estado ``processing`` na
    // legenda usa ``PROCESSING_COLORS`` (tons clarissimos de cinza,
    // grayColor[10]/[12]) onde texto branco seria invisivel. Mantemos
    // shimmer claro com texto cinza escuro — mesma logica do skeleton
    // de cells do Overview e do swatch de modificador na legenda.
    background: `linear-gradient(90deg, ${grayColor[10]} 0%, ${grayColor[12]} 50%, ${grayColor[10]} 100%)`,
    backgroundSize: "120px 100%",
    animation: "$drawerProcessingShimmer 1.4s linear infinite",
    color: grayColor[2],
  },
  statusPillProgress: {
    // in_progress (verde claro #a5d6a7 na legenda). Usamos
    // ``successColor[1]`` (#66bb6a — mesma familia, mais saturado)
    // para que o texto branco fique legivel.
    background: successColor[1],
    color: whiteColor,
  },
  statusPillProgressAttention: {
    // in_progress_attention (laranja claro #ffcc80 na legenda).
    // Variante mais saturada da familia warning para suportar texto
    // branco. Distinto de ``statusPillWarn`` (attention) so na
    // ``saturation`` — mantem a leitura "ainda esperando, com aviso".
    background: warningColor[1], // #ffa726
    color: whiteColor,
  },
  statusPillIdle: {
    // not_sent (cinza neutro). Solido para nao depender do
    // ``clientColor`` por baixo.
    background: grayColor[3],
    color: whiteColor,
  },
  "@keyframes drawerProcessingShimmer": {
    "0%": { backgroundPosition: "-60px 0" },
    "100%": { backgroundPosition: "60px 0" },
  },
  drawerClose: {
    color: whiteColor,
    cursor: "pointer",
    padding: 4,
    borderRadius: 4,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    "&:hover": { background: "rgba(255,255,255,0.12)" },
  },

  // ----- Toolbar ----------------------------------------------------------
  drawerToolbar: {
    display: "flex",
    gap: 8,
    padding: "10px 22px",
    borderBottom: `1px solid ${grayColor[5]}`,
    background: grayColor[13],
    flexShrink: 0,
    alignItems: "center",
  },
  toolbarSpacer: {
    flex: 1,
  },
  toolbarHint: {
    fontSize: 11,
    color: grayColor[1],
    marginRight: 6,
  },
  // Texto separador "- ou -" entre o botao primario e o switch
  // "Marcar como concluido". Reforca para o usuario que sao DUAS
  // alternativas equivalentes (enviar OU marcar como concluido).
  // Aparece junto com o switch e some quando o arquivo ja foi
  // entregue.
  toolbarOr: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    color: grayColor[1],
    fontStyle: "italic",
    userSelect: "none",
    // O "- ou -" precisa ficar visualmente no MEIO entre a borda
    // direita do botao "Enviar arquivo" e o TOGGLE (track) do
    // ``Switch``. Mas o ``Switch`` (size=small) traz ~8-10px de
    // padding interno antes do track aparecer. Resultado: medindo
    // do texto ate o track, temos
    //   esquerda  = gap(8) + marginLeft  = 8 + ML
    //   direita   = marginRight + gap(8) + switchPadding(~8) = MR + 16
    // Para igualar centro visual, marginLeft - marginRight = 8.
    // Manter marginLeft maior empurra o texto pra DIREITA, ficando
    // equidistante do botao e do toggle visivel do switch.
    margin: "0 8px 0 16px",
  },
  // Switch "Marcar como concluido" (waive de input opcional). Vive ao
  // lado do botao primario "Enviar arquivo"; aparece SOMENTE para
  // inputs ``optional=true``. Conteudo: <Switch /> + label discreto.
  // Sem padding lateral para nao quebrar o equilibrio com o
  // ``toolbarOr`` (ver comentario la).
  toolbarWaive: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    borderRadius: 6,
    background: "transparent",
    color: grayColor[1],
    fontSize: 12,
    lineHeight: 1.2,
    userSelect: "none",
  },
  toolbarWaiveSpinner: {
    marginLeft: 6,
    color: grayColor[1],
  },

  // ----- Body (rail + content) -------------------------------------------
  // Wrapper droppable: envolve toolbar + corpo (tudo abaixo do header).
  // Replica o comportamento de coluna flexivel que esses dois filhos
  // tinham como irmaos diretos do ``aside`` (toolbar com flexShrink:0,
  // body com flex:1/minHeight:0). ``position: relative`` ancora o
  // overlay de "solte aqui".
  drawerDropWrap: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  // Overlay "caixa branca pontilhada" exibido durante o drag por cima
  // do corpo do drawer. ``pointer-events: none`` impede que o overlay
  // gere seus proprios dragenter/leave (que fariam o overlay piscar).
  drawerDropOverlay: {
    position: "absolute",
    inset: 10,
    zIndex: 40,
    background: "rgba(255, 255, 255, 0.96)",
    border: `2px dashed ${grayColor[3]}`,
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    pointerEvents: "none",
    color: grayColor[2],
  },
  drawerDropIcon: {
    color: grayColor[4],
    "&.MuiSvgIcon-root": { width: 44, height: 44 },
  },
  drawerDropText: {
    fontSize: 15,
    fontWeight: 500,
    color: grayColor[2],
  },
  drawerBody: {
    display: "grid",
    gridTemplateColumns: "190px 1fr",
    flex: 1,
    minHeight: 0,
  },

  // ----- Rail -------------------------------------------------------------
  // Scroll padronizado com o "status por responsavel" da view principal
  // de inputs (overlay scrollbar — fina, transparente, aparece so no
  // hover). Ver ``inputs_v3_components/styles.js`` (useOverlayScrollStyles).
  rail: {
    background: grayColor[13],
    borderRight: `1px solid ${grayColor[5]}`,
    padding: "10px 0",
    overflowY: "auto",
    "@supports (overflow: overlay)": { overflowY: "overlay" },
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    "&:hover": { scrollbarColor: grayColor[6] + " transparent" },
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: "transparent",
      borderRadius: 3,
      transition: "background 0.2s ease",
    },
    "&:hover::-webkit-scrollbar-thumb": { background: grayColor[6] },
  },
  railGroup: {
    padding: "4px 0 6px",
    "& + &": {
      marginTop: 6,
      borderTop: `1px solid ${grayColor[5]}`,
      paddingTop: 10,
    },
  },
  railGroupLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: grayColor[0],
    padding: "2px 18px 4px",
    fontWeight: 500,
  },
  railItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 18px",
    fontSize: 12.5,
    color: grayColor[1],
    cursor: "pointer",
    borderLeft: "3px solid transparent",
    fontWeight: 400,
    userSelect: "none",
    background: "transparent",
    border: "none",
    borderLeftWidth: 3,
    borderLeftStyle: "solid",
    borderLeftColor: "transparent",
    width: "100%",
    textAlign: "left",
    fontFamily: "inherit",
    "&:hover": { background: grayColor[12] },
    "& .material-icons, & .MuiSvgIcon-root": {
      fontSize: 17,
      color: grayColor[0],
    },
  },
  railItemActive: {
    background: whiteColor,
    color: (props) => props.clientColor || dangerColor[2],
    borderLeftColor: (props) => props.clientColor || dangerColor[2],
    fontWeight: 500,
    "& .material-icons, & .MuiSvgIcon-root": {
      color: (props) => props.clientColor || dangerColor[2],
    },
  },
  dotBadge: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    marginLeft: "auto",
  },
  dotBadgeDanger: { background: dangerColor[2] },
  dotBadgeWarning: { background: warningColor[1] },
  railCount: {
    marginLeft: "auto",
    fontSize: 10,
    background: grayColor[5],
    color: grayColor[1],
    borderRadius: 999,
    padding: "1px 7px",
    fontWeight: 500,
  },

  // ----- Content area ----------------------------------------------------
  content: {
    minWidth: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  // Scroll-area das panes (timeline, agora, etc.). Mesmo padrao overlay
  // do "status por responsavel" — scrollbar transparente que se revela
  // no hover. Combinar com ``useNativeWheelScroll`` quando o mouse-wheel
  // precisar nao propagar para ancestrais.
  paneScroll: {
    overflowY: "auto",
    "@supports (overflow: overlay)": { overflowY: "overlay" },
    flex: 1,
    minHeight: 0,
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    "&:hover": { scrollbarColor: grayColor[6] + " transparent" },
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: "transparent",
      borderRadius: 3,
      transition: "background 0.2s ease",
    },
    "&:hover::-webkit-scrollbar-thumb": { background: grayColor[6] },
  },
  // Combinar com ``paneScroll`` quando o filho ``paneLoading`` precisa
  // de ``flex: 1`` para ocupar a altura disponivel e centralizar o
  // spinner (o painel de inputs nao empilha ``paneHead`` fora do scroll
  // como o drawer de progresso).
  paneScrollFlexColumn: {
    display: "flex",
    flexDirection: "column",
  },
  // Esqueleto/spinner exibido enquanto o NowPane faz o primeiro fetch
  // de notas + control. Evita que a timeline renderize com o item
  // "Periodo aberto..." antes do backend responder (ilusao de estado
  // inicial falso corrigida na Frente 1 da varredura V3).
  paneLoading: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    padding: "48px 24px",
    color: grayColor[1],
  },
  paneLoadingGridWrap: {
    width: 32,
    height: 32,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paneLoadingLabel: {
    fontSize: 12,
    color: grayColor[1],
    letterSpacing: "0.02em",
  },
  // ``DrawerPaneLoadingLayout`` (variant input): sem ``borderBottom`` da
  // ``section``; slot com padding vertical assimétrico p/ centro visual
  // um pouco acima do meio geométrico.
  paneLoadingLayoutHead: {
    flexShrink: 0,
    padding: "14px 24px 8px",
    borderBottom: "none",
  },
  paneLoadingLayoutTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: grayColor[0],
    margin: 0,
  },
  paneLoadingLayoutSlot: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    padding: "12px 24px 92px",
    color: grayColor[1],
  },

  // ----- Botões ----------------------------------------------------------
  btn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "none",
    borderRadius: 3,
    padding: "8px 14px",
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    cursor: "pointer",
    transition: "all 200ms ease",
    "& .material-icons, & .MuiSvgIcon-root": { fontSize: 16 },
    "&:disabled": {
      opacity: 0.55,
      cursor: "not-allowed",
    },
  },
  btnPrimary: {
    background: (props) => props.clientColor || dangerColor[2],
    color: theme.palette.custom?.clientTextColor || whiteColor,
    boxShadow: (props) =>
      `0 2px 4px rgba(${hexToRgbTriplet(props.clientColor)}, 0.2)`,
    "&:hover": {
      background: (props) => darkenHex(props.clientColor || dangerColor[2]),
    },
  },
  btnGhost: {
    background: "transparent",
    color: grayColor[1],
    border: `1px solid ${grayColor[5]}`,
    "&:hover": { background: grayColor[12] },
  },
  btnSmall: {
    fontSize: 11,
    padding: "5px 10px",
  },
  // ``btnDanger`` (leva 97): usado pelos cards de ação destrutiva da
  // aba "ADM > Ações". Vermelho saturado (não usa ``clientColor``)
  // para sinalizar que é uma ação irreversível — independe da
  // identidade visual do cliente.
  btnDanger: {
    background: dangerColor[2],
    color: whiteColor,
    "&:hover": { background: dangerColor[3] || dangerColor[2] },
  },

  // ----- ADM / Ações administrativas (leva 97) ---------------------------
  admActionCard: {
    border: `1px solid ${grayColor[8]}`,
    borderRadius: 8,
    padding: "14px 16px",
    marginBottom: 12,
    background: whiteColor,
    "&:last-child": { marginBottom: 0 },
  },
  admActionCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 6,
  },
  admActionCardIcon: {
    fontSize: "20px !important",
    color: dangerColor[2],
    flexShrink: 0,
  },
  admActionCardTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: grayColor[0],
  },
  admActionCardDescription: {
    fontSize: 12,
    lineHeight: 1.55,
    color: grayColor[1],
    marginBottom: 12,
    "& strong": { color: grayColor[0], fontWeight: 600 },
  },
  admActionCardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  admActionCardDisabledHint: {
    fontSize: 11,
    color: grayColor[2],
    fontStyle: "italic",
  },
  admActionResultOk: {
    marginTop: 10,
    padding: "8px 12px",
    fontSize: 12,
    borderRadius: 6,
    background: "rgba(76, 175, 80, 0.08)",
    border: "1px solid rgba(76, 175, 80, 0.30)",
    color: "#1b5e20",
  },
  admActionResultErr: {
    marginTop: 10,
    padding: "8px 12px",
    fontSize: 12,
    borderRadius: 6,
    background: "rgba(244, 67, 54, 0.08)",
    border: `1px solid rgba(244, 67, 54, 0.30)`,
    color: dangerColor[2],
  },

  // ----- Section / section title -----------------------------------------
  section: {
    padding: "18px 24px",
    borderBottom: `1px solid ${grayColor[8]}`,
    "&:last-child": { borderBottom: "none" },
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: grayColor[0],
    margin: "0 0 14px",
    "& .material-icons, & .MuiSvgIcon-root": { fontSize: 15 },
  },
  sectionCount: {
    background: grayColor[12],
    color: grayColor[1],
    borderRadius: 999,
    padding: "1px 8px",
    fontSize: 10,
    fontWeight: 500,
  },
  sectionHint: {
    fontSize: 11,
    color: grayColor[1],
    marginLeft: "auto",
    textTransform: "none",
    letterSpacing: 0,
    fontWeight: 400,
  },

  // ----- Stages (Estado do processamento) --------------------------------
  // Ordem das chaves importa: ``jss-plugin-nested`` resolve ``$ref``
  // procurando pelos siblings JA declarados. Por isso ``stageVal`` e
  // ``stageTitle`` ficam ANTES dos ``stageCardOk/Warn/Err``, que os
  // referenciam via ``& $stageVal`` (sem isso, JSS emite warning
  // "Could not find the referenced rule 'stageVal'" na primeira
  // attach do stylesheet).
  stages: {
    display: "flex",
    gap: 8,
  },
  stageCard: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 6,
    border: `1px solid ${grayColor[5]}`,
    background: whiteColor,
  },
  stageTitle: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: grayColor[0],
    marginBottom: 4,
    fontWeight: 500,
  },
  stageVal: {
    fontSize: 13,
    color: grayColor[2],
    display: "flex",
    alignItems: "center",
    gap: 5,
    fontWeight: 500,
    "& .material-icons, & .MuiSvgIcon-root": {
      fontSize: 16,
      color: grayColor[0],
    },
  },
  stageCardOk: {
    borderColor: "rgba(76, 175, 80, 0.3)",
    background: "rgba(76, 175, 80, 0.04)",
    "& $stageVal .material-icons, & $stageVal .MuiSvgIcon-root": {
      color: successColor[2],
    },
  },
  stageCardWarn: {
    borderColor: "rgba(255, 152, 0, 0.3)",
    background: "rgba(255, 152, 0, 0.04)",
    "& $stageVal .material-icons, & $stageVal .MuiSvgIcon-root": {
      color: warningColor[1],
    },
  },
  stageCardErr: {
    borderColor: "rgba(244, 67, 54, 0.3)",
    background: "rgba(244, 67, 54, 0.04)",
    "& $stageVal .material-icons, & $stageVal .MuiSvgIcon-root": {
      color: dangerColor[2],
    },
  },
  stageCardWait: {
    opacity: 0.65,
  },

  // ----- Timeline ---------------------------------------------------------
  timeline: {
    padding: "6px 22px 12px 9px",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 29,
      top: 20,
      bottom: 20,
      width: 2,
      background: grayColor[5],
    },
  },
  tlItem: {
    display: "flex",
    gap: 16,
    padding: "14px 0",
    position: "relative",
  },
  tlDot: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: whiteColor,
    border: `2px solid ${grayColor[5]}`,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 8,
    zIndex: 2,
    "& .material-icons, & .MuiSvgIcon-root": {
      fontSize: 13,
      color: grayColor[0],
    },
  },
  tlItemOk: {
    "& $tlDot": { borderColor: successColor[2] },
    "& $tlDot .material-icons, & $tlDot .MuiSvgIcon-root": {
      color: successColor[2],
    },
  },
  tlItemWarn: {
    "& $tlDot": { borderColor: warningColor[1] },
    "& $tlDot .material-icons, & $tlDot .MuiSvgIcon-root": {
      color: warningColor[1],
    },
  },
  tlItemErr: {
    "& $tlDot": { borderColor: dangerColor[2] },
    "& $tlDot .material-icons, & $tlDot .MuiSvgIcon-root": {
      color: dangerColor[2],
    },
  },
  tlItemNote: {
    // Observacao (sev=1). Azul — chama a atencao do consultor sem
    // gritar como um warning (sev=2). Mantemos separado do
    // ``tlItemInfo`` (cinza neutro) porque eles tem semanticas
    // diferentes: ``info`` e marco cronologico neutro (periodo
    // aberto, proxima fase), ``note`` e um achado acionavel do
    // pipeline que o cliente pode optar por endereçar.
    "& $tlDot": { borderColor: infoColor[1] },
    "& $tlDot .material-icons, & $tlDot .MuiSvgIcon-root": {
      color: infoColor[1],
    },
    // ``tlTime`` deliberadamente NAO recebe cor da severidade — o
    // rotulo da timeline ("Leitura do arquivo", "Cruzamento") e
    // categoria estrutural, nao indicador de estado. A cor da
    // severidade fica no dot/icone (a esquerda) e nas caixas de
    // sub-card (abaixo). Antes ``tlTime`` ficava azul aqui, o que
    // gerava descasamento com "Cruzamento" em cinza ao lado.
  },
  tlItemInfo: {
    // O "info" da timeline deve compor com o header escuro do drawer
    // (#353534) — e nao competir com o azul info padrao do sistema.
    // Essa timeline conta a historia do arquivo; ``info`` aqui eh um
    // marco neutro/cronologico (ex.: "Periodo aberto"), nao um estado
    // de sistema. Um tom de cinza escuro comunica "registro" sem
    // chamar atencao como um alerta.
    "& $tlDot": { borderColor: "#353534" },
    "& $tlDot .material-icons, & $tlDot .MuiSvgIcon-root": {
      color: "#353534",
    },
  },
  tlItemAction: {
    "& $tlDot": {
      borderColor: (props) => props.clientColor || dangerColor[2],
      background: (props) => props.clientColor || dangerColor[2],
    },
    "& $tlDot .material-icons, & $tlDot .MuiSvgIcon-root": {
      color: theme.palette.custom?.clientTextColor || whiteColor,
    },
  },
  // Onda 2026-05-17: o ultimo evento da timeline ("Todas as etapas
  // concluidas para este input") encerra visualmente o trilho — a
  // linha conectora vertical (``$timeline::before``) NAO deve
  // ultrapassar a bolinha deste item. Implementacao: um overlay
  // (``::after``) com fundo branco que cobre exatamente a coluna
  // da linha (left=20, width=2, mesma metrica usada no trilho-mae
  // — ``$timeline``) a partir da metade do dot ate alem do fim
  // do item, mascarando o trecho residual da linha que iria
  // aparecer abaixo do dot. ``zIndex:1`` posiciona o overlay
  // acima do trilho (``zIndex`` default 0) e abaixo do dot
  // (``zIndex:2``). Bottom negativo cobre folgas/padding da
  // timeline; como o item terminal e' sempre o ULTIMO, nao ha
  // risco de mascarar um evento futuro.
  tlItemTerminal: {
    "&::after": {
      content: '""',
      position: "absolute",
      left: 20,
      width: 2,
      top: 26,
      bottom: -50,
      background: whiteColor,
      zIndex: 1,
    },
  },
  tlItemHighlighted: {
    background: (props) =>
      `linear-gradient(90deg, rgba(${hexToRgbTriplet(props.clientColor)}, 0.06), transparent 60%)`,
    margin: "4px -9px",
    padding: "14px 9px",
    borderRadius: 4,
  },
  tlBody: {
    flex: 1,
    paddingTop: 2,
    minWidth: 0,
  },
  tlTime: {
    fontSize: 11,
    color: grayColor[0],
  },
  tlTitle: {
    fontSize: 13,
    color: grayColor[2],
    fontWeight: 500,
    marginTop: 2,
    lineHeight: 1.35,
    "& strong": { fontWeight: 500, color: grayColor[2] },
  },
  tlTitleMuted: {
    fontWeight: 400,
    color: grayColor[1],
  },
  tlSub: {
    fontSize: 12,
    color: grayColor[1],
    marginTop: 4,
    lineHeight: 1.55,
    // ``whiteSpace: pre-line`` respeita os ``\n`` que alguns items
    // (ex.: "Arquivo rejeitado" com multiplos motivos) emitem para
    // quebrar linha entre itens da lista.
    whiteSpace: "pre-line",
    "& strong": { color: grayColor[2], fontWeight: 500 },
    "& a": {
      color: (props) => props.clientColor || dangerColor[2],
      cursor: "pointer",
      textDecoration: "none",
      fontWeight: 500,
    },
  },
  // A-014: container das acoes inline embutidas em items da timeline
  // (ex.: botao "Baixar" no item de "Arquivo recebido"). Espaco
  // levemente maior que ``tlSub`` para nao colar no texto, mas ainda
  // dentro do mesmo bloco visual do item.
  tlInlineActions: {
    marginTop: 8,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  // Barra fina de progresso — usada sob o sub do item "Enviando
  // arquivo" para refletir o ``onUploadProgress`` do axios em tempo
  // real. Altura pequena para nao competir visualmente com os textos
  // em volta; a cor segue o tom do tema (clientColor) porque ela
  // comunica "acao do usuario em curso" (e nao um estado de sistema).
  tlProgressTrack: {
    marginTop: 8,
    height: 4,
    width: "100%",
    background: grayColor[8],
    borderRadius: 2,
    overflow: "hidden",
  },
  tlProgressFill: {
    height: "100%",
    background: (props) => props.clientColor || "#81c784",
    borderRadius: 2,
    transition: "width 120ms ease-out",
  },
  tlActions: {
    marginTop: 10,
    display: "flex",
    gap: 8,
  },
  // Lista de sub-cards renderizada DENTRO de um item de timeline
  // (caso CSL): em vez de emitir N items separados na timeline para
  // N cruzamentos verificados, emitimos UM item "Cruzamento(s)" e
  // dentro dele cada cruzamento vira um mini-card com seu proprio
  // titulo + frase humanizada + linha discreta com os limites.
  tlCrossList: {
    marginTop: 8,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  tlCrossCard: {
    padding: "8px 10px 8px 12px",
    borderLeft: `3px solid ${grayColor[5]}`,
    background: "rgba(0, 0, 0, 0.015)",
    borderRadius: 3,
  },
  tlCrossCardWarn: {
    borderLeftColor: warningColor[1],
    background: "rgba(255, 167, 38, 0.06)",
  },
  tlCrossCardErr: {
    borderLeftColor: dangerColor[2],
    background: "rgba(214, 52, 45, 0.06)",
  },
  tlCrossCardOk: {
    borderLeftColor: successColor[2],
    background: "rgba(76, 175, 80, 0.05)",
  },
  // Variante "observacao" (sev=1). Azul/info — informativa, sem
  // tom de alerta. Mesma logica do ``tlItemNote`` no item-mae da
  // timeline: chama atencao do consultor sem gritar como warn.
  tlCrossCardNote: {
    borderLeftColor: infoColor[1],
    background: "rgba(0, 172, 193, 0.05)",
  },
  // Cabecalho de grupo de sub-cards por severidade. Ex.: dentro do
  // item-mae "Cruzamento(s)", separamos as caixas em blocos
  // "Informativos" / "Pontos de atencao" / "Pontos criticos
  // (bloqueantes)". Visual: pequeno, uppercase, cor da severidade
  // (azul / amarelo / vermelho) — funciona como sub-secao discreta
  // dentro do timeline item.
  tlSeverityGroupLabel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginTop: 4,
    marginBottom: -2,
    "&:first-child": {
      marginTop: 0,
    },
  },
  tlSeverityGroupLabelNote: {
    color: infoColor[1],
  },
  tlSeverityGroupLabelWarn: {
    color: warningColor[1],
  },
  tlSeverityGroupLabelErr: {
    color: dangerColor[2],
  },
  // ---------------------------------------------------------------
  // CrossLinksPane (aba "Cruzamentos") — estilos dedicados para a
  // reformulacao da rodada 2026-04-27. Antes a aba reusava ``topic``
  // / ``topicName`` (visual dos Ajustes), o que ficava pobre e
  // tecnico. Aqui temos cards proprios com hierarquia visual e
  // dois campos para os limites (atencao / aceitacao).
  // ---------------------------------------------------------------
  xlinkExplainer: {
    display: "flex",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 6,
    background: "rgba(0, 172, 193, 0.06)",
    border: "1px solid rgba(0, 172, 193, 0.20)",
    marginBottom: 14,
    fontSize: 12,
    lineHeight: 1.55,
    color: grayColor[2],
  },
  xlinkExplainerIcon: {
    fontSize: "16px !important",
    color: infoColor[1],
    marginTop: 1,
    flexShrink: 0,
  },
  xlinkExplainerList: {
    margin: "8px 0 0 0",
    paddingLeft: 18,
    lineHeight: 1.55,
  },
  xlinkExplainerFoot: {
    marginTop: 8,
    color: grayColor[1],
  },
  // ---- Card de cruzamento (rodada 2026-04-28) -------------------------
  // Esta aba descreve **caracteristicas** do cruzamento (definicao +
  // limites), nao o estado vivo do mes — esse fica em "Este mes". Por
  // isso saiu o badge OK/Atencao, ``last_run_at`` e ``role sentence``.
  // O card ganha:
  //   - cadeia de inputs (endpoints em negrito, intermediarios normais)
  //   - subtitulo natural com colunas de cruzamento e ponderacao
  //   - slider read-only com 3 zonas SOLIDAS (verde/amarelo/vermelho)
  //     delimitadas pelos thumbs (limite de atencao e limite critico)
  //   - resumo discreto dos valores e link "Editar limites — em breve"
  // O editor inline (com checkbox "Aplicar retroativo" + Confirmar) e
  // ativado quando a Frente Severity & Thresholds entregar o backend de
  // overrides — ver ``08_severity_rules.md``.
  xlinkCard: {
    border: `1px solid ${grayColor[8]}`,
    borderRadius: 6,
    padding: "14px 16px",
    background: "#fff",
    marginBottom: 10,
    transition: "box-shadow 120ms ease",
    "&:hover": {
      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
    },
  },
  // Linha 1: cadeia de inputs envolvidos.
  // Endpoints em weight 500 (negrito leve) e cor mais firme; bridges
  // em weight 300 com cor mais clara — diferenciacao visual deliberada
  // para o consultor entender de relance "esses sao os inputs do
  // cruzamento" vs "esses sao os intermediarios atravessados".
  xlinkChain: {
    fontSize: 14,
    fontWeight: 300,
    color: grayColor[2],
    lineHeight: 1.45,
    wordBreak: "break-word",
  },
  xlinkChainEndpoint: {
    fontWeight: 500,
    color: grayColor[2],
  },
  xlinkChainArrow: {
    margin: "0 6px",
    color: grayColor[1],
    fontWeight: 300,
  },
  xlinkChainIntermediate: {
    fontWeight: 300,
    color: grayColor[1],
  },
  // Linha 2: colunas de cruzamento e ponderacao em linguagem natural.
  xlinkSubtitle: {
    fontSize: 12,
    color: grayColor[1],
    marginTop: 4,
    lineHeight: 1.5,
    fontWeight: 300,
    "& strong": {
      color: grayColor[2],
      fontWeight: 500,
    },
  },
  // ---- Slider de limites (read-only nesta leva) -----------------------
  xlinkSliderBlock: {
    position: "relative",
    padding: "28px 14px 6px 14px",
    marginTop: 14,
  },
  xlinkSliderRail: {
    position: "relative",
    height: 16,
  },
  xlinkSliderTrack: {
    position: "absolute",
    top: 4,
    left: 0,
    right: 0,
    height: 8,
    borderRadius: 4,
    overflow: "hidden",
    background: grayColor[8],
  },
  xlinkSliderZone: {
    position: "absolute",
    top: 0,
    height: "100%",
  },
  xlinkSliderZoneGreen: {
    background: successColor[0] || "#2e7d32",
  },
  xlinkSliderZoneYellow: {
    background: warningColor[1] || "#ffa726",
  },
  xlinkSliderZoneRed: {
    background: dangerColor[2] || "#d6342d",
  },
  xlinkSliderThumbs: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "100%",
  },
  // Bolinha branca com sombra suave; ficam por cima das zonas e nao
  // sao cortadas pelas pontas arredondadas da track.
  xlinkSliderThumb: {
    position: "absolute",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: 16,
    height: 16,
    borderRadius: "50%",
    background: whiteColor,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.22), 0 0 0 1px rgba(0, 0, 0, 0.06)",
  },
  xlinkSliderThumbLabel: {
    position: "absolute",
    top: -24,
    fontSize: 11,
    fontWeight: 400,
    color: whiteColor,
    background: "#353534",
    padding: "2px 8px",
    borderRadius: 3,
    transform: "translateX(-50%)",
    whiteSpace: "nowrap",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: -3,
      left: "50%",
      transform: "translateX(-50%) rotate(45deg)",
      width: 6,
      height: 6,
      background: "#353534",
    },
  },
  xlinkSliderScale: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 10,
    color: grayColor[1],
    marginTop: 8,
    padding: "0 10px",
    fontWeight: 400,
  },
  // ---- Rodape do card: resumo + link de edicao ------------------------
  xlinkFooter: {
    marginTop: 10,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 10,
    flexWrap: "nowrap",
  },
  xlinkSummary: {
    fontSize: 12,
    color: grayColor[1],
    fontWeight: 300,
  },
  xlinkSummaryWarn: {
    color: warningColor[1] || "#e65100",
    fontWeight: 400,
  },
  xlinkSummaryErr: {
    color: dangerColor[2] || "#c62828",
    fontWeight: 400,
  },
  xlinkSummaryMuted: {
    fontStyle: "italic",
    color: grayColor[3] || "#aaa",
  },
  // Link "Editar limites →" / "Fechar editor ✕" — toggle do editor.
  xlinkEditToggle: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 18,
    border: `1px solid ${grayColor[10]}`,
    borderRadius: 999,
    background: "#fbfcfd",
    padding: "0 7px",
    fontFamily: "inherit",
    fontSize: 10.5,
    fontWeight: 600,
    lineHeight: "16px",
    color: grayColor[1],
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "background 120ms, border-color 120ms, color 120ms",
    "&:hover": {
      borderColor: (props) => props.clientColor || dangerColor[2],
      color: (props) => props.clientColor || dangerColor[2],
      background: whiteColor,
    },
  },
  xlinkEditToggleOpen: {
    borderColor: (props) => props.clientColor || dangerColor[2],
    color: (props) => props.clientColor || dangerColor[2],
  },
  xlinkEvaluationRow: {
    marginTop: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 10,
  },
  xlinkEvaluationText: {
    fontSize: 11.5,
    color: grayColor[1],
    fontWeight: 300,
  },
  // Bolinha "onde cai a avaliacao do mes" — espelha o ``state`` do
  // ``CrossLinkControlV3``. Aparece logo apos o texto
  // "Cruzamento avaliado neste mes: X%", antes do botao de
  // historico. Some quando ``evaluated=false`` (sem run no mes).
  xlinkEvaluationDot: {
    display: "inline-block",
    width: 11,
    height: 11,
    borderRadius: "50%",
    flex: "0 0 auto",
    marginLeft: "-4px",
    marginBottom: 1,
  },
  xlinkEvaluationDotSuccess: {
    background: successColor[1],
  },
  xlinkEvaluationDotWarning: {
    background: warningColor[1],
  },
  xlinkEvaluationDotDanger: {
    background: dangerColor[2],
  },
  xlinkHistoryCompare: {
    marginTop: 8,
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 8,
    overflow: "hidden",
    background: grayColor[11],
  },
  xlinkHistoryHead: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "7px 10px",
    background: whiteColor,
    borderBottom: `1px solid ${grayColor[11]}`,
    fontSize: 11,
    color: grayColor[1],
    "& strong": {
      color: grayColor[2],
      fontWeight: 600,
    },
  },
  xlinkHistoryGrid: {
    display: "grid",
    gridTemplateColumns: "52px repeat(12, minmax(0, 1fr))",
    gap: 1,
    background: grayColor[11],
    width: "100%",
  },
  xlinkHistoryCell: {
    minWidth: 0,
    minHeight: 38,
    background: whiteColor,
    padding: "5px 6px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    justifyContent: "center",
    fontVariantNumeric: "tabular-nums",
  },
  xlinkHistoryCellValid: {
    background: whiteColor,
  },
  xlinkHistoryCellHeader: {
    background: "#f7f8fa",
    color: grayColor[1],
    fontSize: 10,
    fontWeight: 600,
  },
  xlinkHistoryCellYear: {
    background: "#f7f8fa",
    color: grayColor[1],
    fontSize: 10,
    fontWeight: 600,
    alignItems: "flex-end",
    paddingRight: 10,
  },
  xlinkHistoryCellOut: {
    color: grayColor[6],
    background: "#f7f8fa",
    alignItems: "center",
  },
  xlinkHistoryCellCurrent: {
    background: "#eef2f6",
    boxShadow: `inset 0 0 0 1.5px ${grayColor[5]}`,
    position: "relative",
    zIndex: 1,
  },
  xlinkHistoryCellValue: {
    fontSize: 11.5,
    fontWeight: 500,
    lineHeight: 1.15,
    color: grayColor[2],
  },
  xlinkHistoryEmpty: {
    marginTop: 8,
    border: `1px dashed ${grayColor[10]}`,
    borderRadius: 8,
    padding: 12,
    textAlign: "center",
    color: grayColor[1],
    background: "#f7f8fa",
    fontSize: 12,
  },
  // ---- Editor inline (expandido) -------------------------------------
  xlinkEditor: {
    marginTop: 16,
    padding: "14px 16px 12px",
    background: grayColor[19] || "#f1f1f1",
    borderRadius: 6,
    border: `1px solid ${grayColor[8]}`,
  },
  xlinkEditorTitle: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: grayColor[1],
    fontWeight: 500,
    marginBottom: 10,
  },
  xlinkEditorHelp: {
    fontSize: 12,
    color: grayColor[1],
    lineHeight: 1.5,
    marginBottom: 8,
    fontWeight: 300,
  },
  // Linha auxiliar (alinhada a direita) com link "Remover limite
  // critico" / "+ Definir limite critico".
  xlinkCriticalRow: {
    marginTop: 10,
    display: "flex",
    justifyContent: "flex-end",
  },
  xlinkLinkAction: {
    background: "none",
    border: "none",
    fontFamily: "inherit",
    fontSize: 11.5,
    color: grayColor[1],
    cursor: "pointer",
    padding: "2px 4px",
    "&:hover": {
      color: grayColor[2],
      textDecoration: "underline",
    },
  },
  // Checkbox "Aplicar de forma retroativa" — agora INLINE com os
  // botoes de acao (Cancelar / Confirmar) na mesma linha. Ocupa o
  // espaco a esquerda; os botoes ficam alinhados a direita via
  // ``marginLeft: auto``. O texto do checkbox vai em uma unica
  // linha (sem quebra) e o impact text aparece em tooltip-like row
  // logo abaixo apenas quando o checkbox esta marcado.
  xlinkRetroactive: {
    display: "flex",
    gap: 6,
    alignItems: "center",
    fontSize: 12,
    color: grayColor[1],
    lineHeight: 1.4,
    cursor: "pointer",
    fontWeight: 300,
    flex: "1 1 auto",
    minWidth: 0,
    "& input[type='checkbox']": {
      cursor: "pointer",
      flexShrink: 0,
    },
    "& strong": {
      color: grayColor[2],
      fontWeight: 500,
    },
  },
  xlinkRetroactiveImpact: {
    marginTop: 6,
    fontSize: 11.5,
    fontStyle: "italic",
    color: grayColor[1],
    lineHeight: 1.45,
    paddingLeft: 22,
  },
  xlinkEditorActions: {
    marginTop: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  xlinkEditorActionsButtons: {
    display: "flex",
    gap: 8,
    marginLeft: "auto",
  },
  xlinkBtn: {
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 400,
    borderRadius: 4,
    border: "1px solid transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    "&:disabled": {
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },
  xlinkBtnGhost: {
    background: "transparent",
    color: grayColor[1],
    borderColor: grayColor[5] || "#ccc",
    "&:hover:not(:disabled)": {
      background: whiteColor,
    },
  },
  xlinkBtnPrimary: {
    background: (props) => props.clientColor || dangerColor[2],
    color: theme.palette.custom?.clientTextColor || whiteColor,
    borderColor: (props) => props.clientColor || dangerColor[2],
    "&:hover:not(:disabled)": {
      background: (props) => darkenHex(props.clientColor || dangerColor[2]),
      borderColor: (props) => darkenHex(props.clientColor || dangerColor[2]),
    },
  },
  // ---- Slider interativo (cursor e estado de "arrastando") -----------
  xlinkSliderThumbInteractive: {
    cursor: "grab",
    "&:active": {
      cursor: "grabbing",
    },
  },
  // Cabecalho do sub-card de cruzamento — em flex para encaixar o
  // titulo a esquerda e o badge "% qualidade" a direita na mesma
  // linha. ``align-items: baseline`` deixa o numero acompanhar o
  // tracado do titulo mesmo quando o titulo quebra em duas linhas
  // (ex.: nomes longos).
  tlCrossHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 8,
  },
  // Badge "% qualidade" no canto superior direito do sub-card. A
  // cor vem da classe ``tlSeverityGroupLabel*`` injetada inline
  // (azul / amarelo / vermelho), entao aqui so definimos a
  // tipografia. ``flexShrink: 0`` impede o numero de ser
  // espremido pelo titulo longo.
  tlCrossQuality: {
    fontSize: 13,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
    lineHeight: 1.35,
  },
  tlCrossTitle: {
    fontSize: 13,
    color: grayColor[2],
    fontWeight: 500,
    lineHeight: 1.35,
  },
  tlCrossBody: {
    fontSize: 12,
    color: grayColor[1],
    marginTop: 3,
    lineHeight: 1.5,
  },
  // Linha discreta com limites (atencao/aceitacao) e a resposta de
  // decisao quando aplicavel. Mesmo peso visual do ``tlTime`` (11px,
  // cinza) — informacao de referencia, nao o achado.
  tlCrossMeta: {
    fontSize: 11,
    color: grayColor[1],
    marginTop: 4,
    display: "flex",
    flexWrap: "wrap",
    gap: "0 12px",
  },
  // Onda 3.4 (CCU Testes, mai/2026): sub-linha "Decisao: <who>
  // recusou <X> em dd/mm/aa" anexada ao sub-card critico. Visual
  // suave (italico, cinza) para nao competir com o achado principal,
  // mas distinguivel da nota corpo (border-top sutil).
  tlCrossDecision: {
    fontSize: 12,
    color: grayColor[6],
    fontWeight: 400,
    marginTop: 8,
    paddingTop: 6,
    borderTop: `1px dashed ${grayColor[5]}`,
    fontStyle: "italic",
    lineHeight: 1.45,
    "& strong": { fontStyle: "normal", color: grayColor[0] },
  },
  // Onda 2026-05-17: icone inline (thumb up / thumb down) antes da
  // frase "decidiu aceitar/recusar". ``verticalAlign: middle`` para
  // alinhar com a baseline do texto italico ao redor. Cor passada
  // por classe complementar (``tlCrossDecisionIconUp`` /
  // ``...Down``).
  tlCrossDecisionIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
    marginTop: "-1px",
    marginBottom: 1,
    fontStyle: "normal",
  },
  tlCrossDecisionIconUp: {
    color: successColor[2],
  },
  tlCrossDecisionIconDown: {
    color: dangerColor[2],
  },
  // Botao "Copiar lista completa" — link-style, alinhado a direita
  // do sub-card. Discreto (cinza), com underline no hover. Nao
  // compete com o body da nota mas e descobrivel quando o consultor
  // quer extrair os exemplos para uma planilha.
  tlCrossCopyBtn: {
    marginTop: 6,
    background: "none",
    border: "none",
    padding: 0,
    fontSize: 11,
    color: grayColor[1],
    cursor: "pointer",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    "& > span:first-child": {
      textDecoration: "underline dotted",
      textUnderlineOffset: 2,
    },
    "&:hover > span:first-child": {
      color: grayColor[2],
      textDecoration: "underline",
    },
    "&:focus": {
      outline: "none",
      color: grayColor[2],
    },
    "&:disabled": {
      cursor: "wait",
      opacity: 0.85,
    },
  },
  // Acao "Desmarcar" do carimbo "analisado por X" — link discreto inline
  // dentro da linha do carimbo (``tlCrossDecision``). Subtil de proposito:
  // a acao primaria e' marcar; desmarcar e' corrigir um clique errado.
  // Divisor "|" entre acoes inline de um sub-card (ex.: "Baixar chaves
  // nao cruzadas" | "Marcar como analisado"). Fica no fluxo inline e
  // alinha na BASELINE dos botoes ``tlCrossCopyBtn`` (inline-flex) — por
  // isso NAO usa ``inline-block``/``verticalAlign: middle``/``marginTop``,
  // que empurravam o glifo para baixo. Cor um pouco mais clara que o
  // texto dos botoes (``#777``) para ler como separador discreto.
  tlActionDivider: {
    marginLeft: 8,
    marginRight: 8,
    color: grayColor[3],
    fontSize: 11,
    userSelect: "none",
  },
  tlMarkUndoBtn: {
    background: "none",
    border: "none",
    padding: 0,
    marginLeft: 4,
    fontSize: 11,
    fontFamily: "inherit",
    color: grayColor[1],
    cursor: "pointer",
    textDecoration: "underline dotted",
    textUnderlineOffset: 2,
    "&:hover": { color: grayColor[2], textDecoration: "underline" },
    "&:focus": { outline: "none", color: grayColor[2] },
    "&:disabled": { cursor: "wait", opacity: 0.6 },
  },
  // Spinner inline (~tamanho do texto) que aparece a direita do label
  // enquanto o download esta em curso. Sem fallback "Baixado!" — a
  // chegada do save-as do browser ja e o feedback de sucesso, e o
  // estado de erro mantem o botao no neutro (falha silenciosa).
  "@keyframes tlCrossSpin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  tlCrossCopySpinner: {
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: "50%",
    border: `1.5px solid ${grayColor[7] || "#bbb"}`,
    borderTopColor: grayColor[1],
    animation: "$tlCrossSpin 0.75s linear infinite",
  },
  // Estado de reveal progressivo (narrativa pós-upload): o item começa
  // escondido e entra com fade + leve translate. Classe removida assim
  // que o reveal avança para este index, deixando o item em sua posição
  // final sem transform residual.
  tlItemHidden: {
    opacity: 0,
    transform: "translateY(6px)",
    pointerEvents: "none",
    transition: "none",
  },
  tlItemEntering: {
    opacity: 1,
    transform: "translateY(0)",
    transition: "opacity 320ms ease, transform 320ms ease",
  },
  // Linha narrativa transitória ("Lendo estrutura do arquivo...",
  // "Cruzando chaves com os cadastros..."). Visual deliberadamente mais
  // leve que um tlItem normal: dot com spinner CSS em vez de ícone.
  tlItemPending: {
    "& $tlDot": {
      borderColor: grayColor[3],
      background: whiteColor,
    },
  },
  tlSpinnerDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    border: `2px solid ${grayColor[5]}`,
    borderTopColor: (props) => props.clientColor || dangerColor[2],
    animation: "$tlSpin 900ms linear infinite",
  },
  "@keyframes tlSpin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  stageCardRunning: {
    borderColor: (props) => `rgba(${hexToRgbTriplet(props.clientColor)}, 0.35)`,
    background: (props) => `rgba(${hexToRgbTriplet(props.clientColor)}, 0.04)`,
    "& $stageVal": {
      color: (props) => props.clientColor || dangerColor[2],
    },
    "& $stageVal .material-icons, & $stageVal .MuiSvgIcon-root": {
      color: (props) => props.clientColor || dangerColor[2],
      animation: "$stagePulse 1200ms ease-in-out infinite",
    },
  },
  "@keyframes stagePulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.45 },
  },

  // ----- Decision (D-22) -------------------------------------------------
  decision: {
    border: (props) =>
      `1px solid rgba(${hexToRgbTriplet(props.clientColor)}, 0.35)`,
    background: (props) =>
      `linear-gradient(180deg, rgba(${hexToRgbTriplet(props.clientColor)}, 0.05), rgba(${hexToRgbTriplet(props.clientColor)}, 0.02))`,
    borderRadius: 6,
    padding: "16px 18px",
  },
  decisionLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 10,
    textTransform: "uppercase",
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: (props) => props.clientColor || dangerColor[2],
    marginBottom: 8,
    "& .material-icons, & .MuiSvgIcon-root": { fontSize: 14 },
  },
  decisionQuestion: {
    fontSize: 14,
    fontWeight: 500,
    color: grayColor[2],
    marginBottom: 12,
    lineHeight: 1.4,
  },
  decisionOptions: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  decisionOption: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 4,
    border: `1px solid ${grayColor[5]}`,
    background: whiteColor,
    cursor: "pointer",
    transition: "all 200ms ease",
    width: "100%",
    textAlign: "left",
    fontFamily: "inherit",
    "&:hover": {
      borderColor: (props) => props.clientColor || dangerColor[2],
      background: (props) =>
        `rgba(${hexToRgbTriplet(props.clientColor)}, 0.03)`,
    },
  },
  decisionOptionSuggested: {
    borderColor: "rgba(76, 175, 80, 0.6)",
    background: "rgba(76, 175, 80, 0.04)",
    "& $decisionRadio": {
      borderColor: successColor[2],
      background: `radial-gradient(circle at center, ${successColor[2]} 0 4px, transparent 5px)`,
    },
  },
  decisionRadio: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: `2px solid ${grayColor[3]}`,
    flexShrink: 0,
    marginTop: 2,
  },
  decisionRadioChecked: {
    borderColor: (props) => props.clientColor || dangerColor[2],
    background: (props) =>
      `radial-gradient(circle at center, ${props.clientColor || dangerColor[2]} 0 4px, transparent 5px)`,
  },
  decisionOptionBody: {
    flex: 1,
    minWidth: 0,
  },
  decisionOptionTitle: {
    fontWeight: 500,
    fontSize: 13,
    color: grayColor[2],
    "& strong": { fontWeight: 500 },
  },
  decisionOptionTag: {
    display: "inline-block",
    fontSize: 9,
    padding: "1px 6px",
    borderRadius: 999,
    background: successColor[2],
    color: whiteColor,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginLeft: 6,
    fontWeight: 700,
  },
  decisionOptionTooltip: {
    fontSize: 12,
    color: grayColor[1],
    marginTop: 3,
  },
  decisionFooter: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 14,
    fontSize: 12,
    color: grayColor[1],
  },

  // ----- Toggle slider ---------------------------------------------------
  toggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    userSelect: "none",
    fontSize: 12,
    color: grayColor[1],
    margin: 0,
  },
  toggleDisabled: {
    cursor: "not-allowed",
    opacity: 0.65,
  },
  toggleLabel: {
    lineHeight: "18px",
    transform: "translateY(1px)",
  },
  toggleInput: {
    position: "absolute",
    opacity: 0,
    pointerEvents: "none",
  },
  toggleSlider: {
    position: "relative",
    width: 34,
    height: 18,
    background: grayColor[5],
    borderRadius: 999,
    transition: "all 200ms ease",
    flexShrink: 0,
    "&::after": {
      content: '""',
      position: "absolute",
      top: 2,
      left: 2,
      width: 14,
      height: 14,
      background: whiteColor,
      borderRadius: "50%",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
      transition: "all 200ms ease",
    },
  },
  toggleSliderOn: {
    background: (props) => props.clientColor || dangerColor[2],
    "&::after": { left: 18 },
  },

  // ----- Markdown editor --------------------------------------------------
  mdEditor: {
    border: `1px solid ${grayColor[5]}`,
    borderRadius: 6,
    overflow: "hidden",
    background: whiteColor,
  },
  mdTabs: {
    display: "flex",
    background: grayColor[13],
    borderBottom: `1px solid ${grayColor[5]}`,
    padding: "0 8px",
  },
  mdTab: {
    padding: "9px 14px",
    fontSize: 12,
    fontWeight: 500,
    color: grayColor[1],
    cursor: "pointer",
    borderBottom: "2px solid transparent",
    marginBottom: -1,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    userSelect: "none",
    background: "transparent",
    border: "none",
    fontFamily: "inherit",
    "&:hover": { color: grayColor[2] },
    "& .material-icons, & .MuiSvgIcon-root": { fontSize: 14 },
  },
  mdTabActive: {
    color: (props) => props.clientColor || dangerColor[2],
    borderBottomColor: (props) => props.clientColor || dangerColor[2],
    background: whiteColor,
  },
  mdToolbar: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    padding: "6px 10px",
    background: grayColor[13],
    borderBottom: `1px solid ${grayColor[5]}`,
    flexWrap: "wrap",
  },
  mdBtn: {
    width: 28,
    height: 28,
    borderRadius: 4,
    border: "none",
    background: "transparent",
    color: grayColor[1],
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "inherit",
    "&:hover": { background: grayColor[8], color: grayColor[2] },
    "& .material-icons, & .MuiSvgIcon-root": { fontSize: 16 },
  },
  mdBtnBold: { fontWeight: 700 },
  mdBtnItalic: { fontStyle: "italic" },
  mdBtnHeading: { fontWeight: 700, fontSize: 13 },
  mdSep: {
    width: 1,
    height: 18,
    background: grayColor[5],
    margin: "0 4px",
  },
  mdTextarea: {
    width: "100%",
    minHeight: 200,
    border: "none",
    padding: "14px 16px",
    fontFamily: '"SF Mono","Monaco","Consolas",monospace',
    fontSize: 13,
    lineHeight: 1.55,
    resize: "vertical",
    color: grayColor[2],
    outline: "none",
    background: whiteColor,
    display: "block",
    "&:focus": { background: "#fffdf7" },
  },
  mdEditorFooter: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 12px",
    background: grayColor[13],
    borderTop: `1px solid ${grayColor[5]}`,
    fontSize: 11,
    color: grayColor[1],
    flexWrap: "wrap",
  },
  mdHintInline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    "& code": {
      background: grayColor[8],
      padding: "1px 5px",
      borderRadius: 3,
      fontSize: 11,
      color: grayColor[2],
      fontFamily: '"SF Mono","Monaco","Consolas",monospace',
    },
  },
  mdSpacer: { flex: 1 },
  // Markdown renderizado — preview do editor e visualização read-only.
  // Tamanhos de heading calibrados para ficar proporcionais ao corpo
  // (13px); nunca os defaults gigantes do browser.
  mdPreviewPane: {
    padding: "16px 18px",
    minHeight: 200,
    fontSize: 13,
    lineHeight: 1.6,
    color: grayColor[2],
    "& > *:first-child": { marginTop: 0 },
    "& > *:last-child": { marginBottom: 0 },
    "& p": { margin: "0 0 10px" },
    "& ul, & ol": { margin: "0 0 10px", paddingLeft: 20 },
    "& li": { marginBottom: 4 },
    "& code": {
      background: grayColor[8],
      padding: "1px 5px",
      fontSize: 12,
      fontFamily: '"SF Mono","Monaco","Consolas",monospace',
      color: grayColor[2],
    },
    "& pre": {
      background: grayColor[13],
      padding: "10px 12px",
      overflow: "auto",
      fontSize: 12,
      fontFamily: '"SF Mono","Monaco","Consolas",monospace',
      margin: "10px 0",
    },
    "& blockquote": {
      margin: "12px 0",
      padding: "4px 12px",
      borderLeft: `3px solid ${grayColor[5]}`,
      color: grayColor[1],
      background: grayColor[13],
    },
    "& a": {
      color: (props) => props.clientColor || dangerColor[2],
      fontWeight: 500,
      cursor: "pointer",
    },
    "& h1": {
      fontSize: 16,
      fontWeight: 500,
      margin: "16px 0 8px",
      color: grayColor[2],
    },
    "& h2": {
      fontSize: 14,
      fontWeight: 500,
      margin: "14px 0 6px",
      color: grayColor[2],
    },
    "& h3, & h4, & h5, & h6": {
      fontSize: 13,
      fontWeight: 500,
      margin: "12px 0 4px",
      color: grayColor[2],
      textTransform: "none",
      letterSpacing: 0,
    },
    "& hr": {
      border: "none",
      borderTop: `1px solid ${grayColor[8]}`,
      margin: "14px 0",
    },
    "& table": {
      borderCollapse: "collapse",
      margin: "10px 0",
      fontSize: 12,
    },
    "& th, & td": {
      border: `1px solid ${grayColor[8]}`,
      padding: "4px 8px",
    },
    "& th": { background: grayColor[13], fontWeight: 500 },
  },
  // Bloco "instruções" read-only: herda tipografia do mdPreviewPane e
  // apenas acrescenta a borda-esquerda do cliente como destaque.
  instructionsBody: {
    background: grayColor[13],
    borderLeft: (props) => `3px solid ${props.clientColor || dangerColor[2]}`,
  },

  // ----- Messages ---------------------------------------------------------
  message: {
    display: "flex",
    gap: 10,
    padding: "10px 12px",
    background: grayColor[13],
    fontSize: 13,
    color: grayColor[2],
    borderLeft: `3px solid ${grayColor[5]}`,
    marginBottom: 8,
    "& .material-icons, & .MuiSvgIcon-root": {
      fontSize: 18,
      marginTop: 1,
    },
  },
  messageWarn: {
    background: "rgba(255, 152, 0, 0.06)",
    borderLeftColor: warningColor[1],
    "& .material-icons, & .MuiSvgIcon-root": { color: warningColor[1] },
  },
  messageErr: {
    background: "rgba(244, 67, 54, 0.05)",
    borderLeftColor: dangerColor[2],
    "& .material-icons, & .MuiSvgIcon-root": { color: dangerColor[2] },
  },
  messageInfo: {
    background: "rgba(0, 172, 193, 0.05)",
    borderLeftColor: infoColor[1],
    "& .material-icons, & .MuiSvgIcon-root": { color: infoColor[1] },
  },
  msgTitle: {
    fontWeight: 500,
    lineHeight: 1.3,
  },
  msgBody: {
    fontSize: 12,
    color: grayColor[1],
    marginTop: 3,
    lineHeight: 1.55,
    "& a": {
      color: (props) => props.clientColor || dangerColor[2],
      cursor: "pointer",
    },
  },

  // ----- Chips (cross-links / config) ------------------------------------
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 11px",
    background: grayColor[12],
    color: grayColor[2],
    borderRadius: 999,
    fontSize: 12,
    margin: "0 4px 6px 0",
    cursor: "pointer",
    border: "none",
    fontFamily: "inherit",
    "&:hover": { background: grayColor[8] },
    "& .material-icons, & .MuiSvgIcon-root": {
      fontSize: 13,
      color: grayColor[0],
    },
  },
  chipRole: {
    fontSize: 9,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    background: grayColor[5],
    color: grayColor[1],
    padding: "1px 5px",
    borderRadius: 3,
    fontWeight: 500,
  },
  chipRoleOk: {
    background: "rgba(76, 175, 80, 0.18)",
    color: "#2e7d32",
  },
  chipRoleWarn: {
    background: "rgba(255, 152, 0, 0.22)",
    color: "#b45c00",
  },

  // ----- History items ---------------------------------------------------
  historyItem: {
    display: "flex",
    gap: 12,
    padding: "12px 0",
    borderBottom: `1px solid ${grayColor[8]}`,
    "&:last-child": { borderBottom: "none" },
  },
  historyAvatar: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: grayColor[5],
    color: grayColor[1],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 500,
    flexShrink: 0,
  },
  historyBody: {
    flex: 1,
    minWidth: 0,
  },
  historyTitle: {
    fontSize: 13,
    color: grayColor[2],
    fontWeight: 500,
  },
  historyMeta: {
    fontSize: 11,
    color: grayColor[1],
    marginTop: 2,
  },
  historyCurrentBadge: {
    display: "inline-block",
    fontSize: 9,
    padding: "1px 6px",
    borderRadius: 999,
    background: successColor[2],
    color: whiteColor,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontWeight: 700,
    marginLeft: 6,
  },
  historyRollback: {
    fontSize: 11,
    color: (props) => props.clientColor || dangerColor[2],
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontWeight: 500,
    background: "transparent",
    border: "none",
    fontFamily: "inherit",
    padding: 0,
    "&:hover": { textDecoration: "underline" },
  },
  // Container das acoes (Baixar / Usar arquivo) na direita do item.
  historyActions: {
    display: "inline-flex",
    gap: 8,
    alignItems: "center",
    flexShrink: 0,
  },
  // Resultado do parsing por linha (DA-001: ok / atencao / erro).
  // Mesmo color code dos buckets canonicos (D-33), mas em forma de
  // pill compacta dentro do titulo do item.
  historyResultBadge: {
    display: "inline-block",
    fontSize: 9,
    padding: "1px 6px",
    borderRadius: 999,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontWeight: 700,
    marginLeft: 6,
    color: whiteColor,
  },
  historyResultOk: { background: successColor[3] },
  historyResultWarn: { background: "#f59e0b" },
  historyResultErr: { background: dangerColor[2] },
  historyResultInfo: { background: grayColor[2] },
  historyEmpty: {
    fontSize: 12,
    color: grayColor[1],
    padding: "10px 0",
    fontStyle: "italic",
  },

  // ----- Topics (About / Config) -----------------------------------------
  topic: {
    padding: "10px 0",
    borderBottom: `1px solid ${grayColor[8]}`,
    display: "flex",
    gap: 20,
    "&:last-child": { borderBottom: "none" },
  },
  topicName: {
    flex: "0 0 150px",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: grayColor[0],
    fontWeight: 500,
  },
  topicContent: {
    flex: 1,
    fontSize: 13,
    color: grayColor[2],
    "& code": {
      background: grayColor[8],
      padding: "1px 5px",
      borderRadius: 3,
      fontSize: 12,
      fontFamily: '"SF Mono","Monaco","Consolas",monospace',
      color: grayColor[2],
    },
  },

  // ----- CheckResultV3 (D-23) --------------------------------------------
  // Cada CheckResult vira um "cartão" dentro da timeline. A cor da borda
  // esquerda reflete o status; o restante é montado com pedacinhos
  // reutilizáveis (impact badges, artifact row, note inline).
  checkResult: {
    marginTop: 10,
    border: `1px solid ${grayColor[8]}`,
    background: whiteColor,
    borderLeftWidth: 3,
    borderLeftStyle: "solid",
    borderLeftColor: grayColor[5],
  },
  checkResultOk: { borderLeftColor: successColor[2] },
  checkResultWarn: { borderLeftColor: warningColor[1] },
  checkResultErr: { borderLeftColor: dangerColor[2] },
  checkResultHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    padding: "9px 12px",
    borderBottom: `1px solid ${grayColor[8]}`,
  },
  checkResultTitle: {
    fontSize: 12.5,
    fontWeight: 500,
    color: grayColor[2],
    flex: 1,
    minWidth: 0,
  },
  checkResultParticipants: {
    fontSize: 11,
    color: grayColor[1],
  },
  checkResultBody: {
    padding: "10px 12px",
    fontSize: 12,
    color: grayColor[1],
    lineHeight: 1.55,
    "& p": { margin: "0 0 6px" },
    "& p:last-child": { marginBottom: 0 },
    "& strong": { fontWeight: 500, color: grayColor[2] },
    "& a": {
      color: (props) => props.clientColor || dangerColor[2],
      cursor: "pointer",
      fontWeight: 500,
      textDecoration: "none",
    },
  },
  impactRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    padding: "0 12px 10px",
  },
  impactBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    padding: "3px 8px",
    borderRadius: 999,
    background: grayColor[13],
    color: grayColor[1],
    border: `1px solid ${grayColor[8]}`,
    "& strong": {
      color: grayColor[2],
      fontWeight: 500,
    },
  },
  impactBadgeWarn: {
    background: "rgba(255, 152, 0, 0.08)",
    borderColor: "rgba(255, 152, 0, 0.25)",
    color: "#8a4b00",
    "& strong": { color: "#b45c00" },
  },
  impactBadgeErr: {
    background: "rgba(244, 67, 54, 0.06)",
    borderColor: "rgba(244, 67, 54, 0.25)",
    color: "#8a1f1a",
    "& strong": { color: dangerColor[2] },
  },
  checkNotes: {
    padding: "0 12px 10px",
    margin: 0,
    listStyle: "none",
    fontSize: 12,
    color: grayColor[1],
    "& li": {
      position: "relative",
      paddingLeft: 14,
      marginBottom: 3,
      lineHeight: 1.5,
    },
    "& li::before": {
      content: '"•"',
      position: "absolute",
      left: 0,
      color: grayColor[3],
    },
    "& strong": { color: grayColor[2], fontWeight: 500 },
  },
  checkArtifacts: {
    padding: "0 12px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  artifactRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    background: grayColor[13],
    borderRadius: 4,
    border: `1px solid ${grayColor[8]}`,
    "& .material-icons, & .MuiSvgIcon-root": {
      fontSize: 18,
      color: grayColor[0],
      flexShrink: 0,
    },
  },
  artifactBody: {
    flex: 1,
    minWidth: 0,
  },
  artifactFilename: {
    fontSize: 12,
    color: grayColor[2],
    fontWeight: 500,
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  artifactSummary: {
    fontSize: 11,
    color: grayColor[1],
    marginTop: 2,
    lineHeight: 1.45,
    "& strong": { color: grayColor[2], fontWeight: 500 },
  },
  artifactRoleTag: {
    fontSize: 9,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    background: grayColor[5],
    color: grayColor[1],
    padding: "1px 5px",
    borderRadius: 3,
    fontWeight: 500,
    flexShrink: 0,
  },
  artifactDownload: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontWeight: 500,
    color: (props) => props.clientColor || dangerColor[2],
    background: "transparent",
    border: "none",
    padding: "4px 8px",
    cursor: "pointer",
    fontFamily: "inherit",
    "&:hover": { background: grayColor[12] },
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
  },
  feedsRow: {
    padding: "0 12px 10px",
    fontSize: 11,
    color: grayColor[1],
    "& strong": { color: grayColor[2], fontWeight: 500 },
  },

  // ----- Utilities --------------------------------------------------------
  muted: { color: grayColor[1] },
  hint: { fontSize: 11, color: grayColor[1] },
  placeholderPane: {
    padding: "80px 48px",
    textAlign: "center",
    color: grayColor[1],
    fontSize: 13,
    "& .material-icons, & .MuiSvgIcon-root": {
      fontSize: 48,
      color: grayColor[3],
      marginBottom: 10,
    },
  },

  // ----- Tag "Acao necessaria" (topo do NowPane) -------------------------
  // Aparece como uma faixa estreita acima do DecisionsCard / da timeline
  // quando de fato ha bloqueio (pergunta nao respondida, arquivo
  // rejeitado). Nao serve como repeticao de TUDO que esta amarelo —
  // so o que de fato precisa de acao do consultor.
  actionRequiredTag: {
    // Antes: ``inline-flex`` + ``width: fit-content`` (pílula compacta colada
    // na borda esquerda do ``paneScroll``, que não tem padding horizontal).
    // Agora: ocupa a largura do conteúdo respeitando o mesmo padding
    // horizontal das ``section`` (24px), com flex em linha cheia para
    // o ícone + texto ficarem alinhados à esquerda mas o container
    // "pintando" a faixa inteira do drawer.
    display: "flex",
    alignItems: "center",
    gap: 10,
    margin: "12px 24px 6px",
    padding: "10px 16px",
    borderRadius: 10,
    background: "rgba(244, 67, 54, 0.08)",
    border: `1px solid rgba(244, 67, 54, 0.30)`,
    color: dangerColor[2],
    fontSize: 12,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  actionRequiredTagIcon: {
    fontSize: "18px !important",
    flexShrink: 0,
  },

  // Badge "Input opcional" (azul). Espelha o ``actionRequiredTag``
  // (mesma posicao + tipografia) mas em cor informativa, nao alarme.
  // Aparece no topo da NowPane sempre que ``input.optional === true``,
  // para que o consultor saiba que o cálculo aceita ausencia + pode
  // usar o switch "Marcar como concluido" do toolbar para avançar.
  optionalInputTag: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    margin: "12px 24px 6px",
    padding: "10px 16px",
    borderRadius: 10,
    background: "rgba(0, 172, 193, 0.08)",
    border: `1px solid rgba(0, 172, 193, 0.30)`,
    color: infoColor[2],
    fontSize: 12,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  optionalInputTagIcon: {
    fontSize: "18px !important",
    flexShrink: 0,
  },
  optionalInputTagText: {
    flex: 1,
    minWidth: 0,
  },
  optionalInputTagSubtitle: {
    display: "block",
    fontSize: 11,
    fontWeight: 400,
    textTransform: "none",
    letterSpacing: 0,
    color: grayColor[1],
    marginTop: 2,
  },

  decisionsStack: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    margin: "16px 24px 20px",
  },
  decisionsCard: {
    padding: 0,
    border: `1px solid ${dangerColor[0]}`,
    borderRadius: 10,
    background: "#f5f5f5",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
  },
  decisionsCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 18px 6px 12px",
    backgroundImage: `repeating-linear-gradient(
      135deg,
      ${dangerColor[0]},
      ${dangerColor[0]} 8px,
      ${dangerColor[0]}e5 8px,
      ${dangerColor[0]}e5 16px
    )`,
    "& > .MuiSvgIcon-root": {
      fontSize: 22,
      color: "#fff",
      flexShrink: 0,
    },
  },
  decisionsCardTitle: {
    fontSize: 16,
    fontWeight: 400,
    color: "#fff",
    letterSpacing: 0,
  },
  decisionsCardSubtitle: {
    color: "#fff",
    fontSize: 12,
    fontWeight: 400,
    marginLeft: "auto",
  },
  decisionItem: {
    padding: "0",
    "& + &": { borderTop: `1px dashed ${grayColor[4] || "#e0e0e0"}` },
  },
  pendingDecisionBody: {
    padding: "12px 14px 0",
  },
  pendingDecisionMeta: {
    fontSize: 12,
    color: grayColor[2],
    marginBottom: 6,
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
    "& .MuiSvgIcon-root": { fontSize: 14 },
  },
  pendingDecisionQuestion: {
    fontSize: 14,
    color: grayColor[7],
    marginBottom: 12,
    "& strong": { color: grayColor[7], fontWeight: 600 },
  },
  // Bloco compacto de stats (cobertura medida vs limite vs criticidade).
  // Aparece quando o backend manda ``measure`` no payload — fica como
  // "leitura rapida" para quem ja conhece a metrica e nao precisa do
  // enunciado completo.
  pendingDecisionStats: {
    display: "inline-flex",
    gap: 16,
    padding: "8px 12px",
    background: "#f3f3f3",
    borderRadius: 6,
    fontSize: 12,
    marginBottom: 4,
    flexWrap: "wrap",
  },
  pendingDecisionStatKey: { color: grayColor[1] },
  pendingDecisionStatValue: {
    color: grayColor[0],
    fontWeight: 600,
    marginLeft: 4,
  },
  pendingDecisionStatValueBad: { color: dangerColor[0] },
  pendingDecisionChoices: {
    // Onda 3.4 (CCU Testes, mai/2026): flex (em vez de grid
    // auto-fit) com ``flex: 1`` em cada botao + ``minWidth: 0``
    // garante que o conjunto SEMPRE preencha a largura disponivel do
    // card, dividindo igualmente entre 2..N opcoes. ``flexWrap``
    // continua permitindo quebra de linha se o texto for muito
    // longo, mas a regra default e' uma linha unica de botoes
    // larguras iguais.
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    padding: "12px 12px",
  },
  pendingDecisionChoice: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: 14,
    flex: "1 1 0",
    minWidth: 0,
    // border: `1.5px solid ${grayColor[5]}`,
    border: "0",
    borderRadius: 8,
    background: whiteColor,
    cursor: "pointer",
    transition: "all 160ms ease",
    textAlign: "left",
    fontFamily: "inherit",
    "&:hover:not(:disabled)": {
      // boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      // transform: "translateY(-1px)",
      transform: "scale(1.03)",
    },
    "&:disabled": { opacity: 0.55, cursor: "not-allowed", transform: "none" },
  },
  pendingDecisionChoiceAccept: {
    // borderColor: "rgba(76, 175, 80, 0.5)",
    background: "rgba(76, 175, 80, 0.1)",
  },
  pendingDecisionChoiceReject: {
    // borderColor: "rgba(244, 67, 54, 0.35)",
    background: "rgba(244, 67, 54, 0.1)",
  },
  pendingDecisionChoiceIcon: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: whiteColor,
    "& .MuiSvgIcon-root": { fontSize: 18 },
  },
  pendingDecisionChoiceIconAccept: { background: successColor[0] },
  pendingDecisionChoiceIconReject: { background: dangerColor[0] },
  pendingDecisionChoiceLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: grayColor[0],
  },
  pendingDecisionChoiceEffect: {
    fontSize: 11.5,
    color: grayColor[1],
    lineHeight: 1.4,
  },
  pendingDecisionSuggestedBadge: {
    position: "absolute",
    top: -8,
    right: 10,
    fontSize: 9.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: whiteColor,
    background: warningColor[0],
    padding: "2px 8px",
    borderRadius: 10,
  },
  inlineCode: {
    fontFamily:
      "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
    fontSize: "0.9em",
    background: grayColor[12] || "#f6f7f8",
    border: `1px solid ${grayColor[5]}`,
    padding: "1px 5px",
    borderRadius: 3,
    color: "#1f2933",
  },
  decisionError: {
    marginTop: 8,
    padding: "6px 10px",
    borderRadius: 4,
    background: "rgba(244, 67, 54, 0.08)",
    color: dangerColor[0],
    fontSize: 12,
  },
}));

export default useDrawerStyles;
